import { CourseEnrollment } from '@/models/CourseEnrollment';
import { LanguageType } from '@/models/LanguageType';
import { EvaluationLesson, Lesson } from '@/models/Lesson';
import { LessonEnrollment } from '@/models/LessonEnrollment';
import { Person } from '@/models/Person';
import { ProgramClass } from '@/models/ProgramClass';
import { Status } from '@/models/Status';
import { CourseStudentView } from '@/store/list/types';
import { LocaleData } from '@/translations/LocaleData';
import { differenceInDays, isBefore } from 'date-fns';
import Constants from '../Constants';
import { formatDate } from './UtilService';

export class LmsCourseService {
    getCourseStatus = (courseEnrollment: CourseEnrollment, programClass: ProgramClass, strings: LocaleData): string => {
        if (courseEnrollment === null || programClass === null) {
            return strings.COURSE_NOT_STARTED;
        }

        switch (courseEnrollment.status) {
            case Constants.courseEnrollmentStatus.NOT_STARTED:
                return programClass.finishDate !== null && this.courseIsExpired(programClass.finishDateAsString)
                    ? strings.COURSE_EXPIRED
                    : strings.COURSE_NOT_STARTED;
            case Constants.courseEnrollmentStatus.STARTED:
                return programClass.finishDate !== null && this.courseIsExpired(programClass.finishDateAsString)
                    ? strings.COURSE_EXPIRED
                    : strings.COURSE_STARTED;
            case Constants.courseEnrollmentStatus.COMPLETED:
                return strings.COURSE_COMPLETED;
            case Constants.courseEnrollmentStatus.PASSED:
                return strings.COURSE_PASSED;
            case Constants.courseEnrollmentStatus.FAILED:
                return strings.COURSE_FAILED;
            case Constants.courseEnrollmentStatus.BLOCKED:
                return strings.COURSE_BLOCKED;
            default:
                return strings.COURSE_BLOCKED;
        }
    };

    getDaysToExpire = (programClass: ProgramClass, strings: LocaleData): string => {
        const daysToExpire = differenceInDays((programClass.finishDateAsString as unknown) as Date, Date.now());
        const formattedDate = formatDate(programClass.finishDate || new Date(), strings._LANGUAGE as LanguageType);

        if (daysToExpire > 30) {
            return `${strings.UNTIL} ${formattedDate}`;
        }

        return `${strings.EXPIRES_IN} ${daysToExpire} ${strings.DAYS.toLocaleLowerCase()}`;
    };

    courseIsExpired = (finishDateAsString: string): boolean =>
        finishDateAsString !== null && isBefore((finishDateAsString as unknown) as Date, Date.now());

    courseIsBlocked = (status: Status): boolean => status === Constants.courseEnrollmentStatus.BLOCKED;

    disableCard = (course: CourseStudentView): boolean =>
        this.courseIsExpired(course.classEnrollment.programClass.finishDateAsString) || this.courseIsBlocked(course.courseEnrollment.status);

    getLessonStatus = (courseEnrollment: CourseEnrollment, lesson: Lesson, strings: LocaleData): string => {
        if (!courseEnrollment && lesson.prerequisites) {
            return strings.BLOCKED;
        }

        if (!courseEnrollment || !courseEnrollment.lessonEnrollments) {
            return strings.NOT_ATTEMPTED;
        }

        const lessonEnrollment: LessonEnrollment = courseEnrollment.lessonEnrollments.find(
            (lEnrollment: LessonEnrollment) => lEnrollment.lesson.id === lesson.id,
        ) as LessonEnrollment;

        if (!lessonEnrollment.prerequisitesMet) {
            return strings.BLOCKED;
        }

        switch (lessonEnrollment.status) {
            case Constants.lessonEnrollmentStatus.COMPLETED:
                return strings.CONCLUDED;
            case Constants.lessonEnrollmentStatus.INCOMPLETE:
                return strings.INCOMPLETE;
            case Constants.lessonEnrollmentStatus.NOT_ATTEMPTED:
                return strings.NOT_ATTEMPTED;
            case Constants.lessonEnrollmentStatus.FAILED:
                return strings.FAILED;
            case Constants.lessonEnrollmentStatus.PASSED:
                return strings.PASSED;
            default:
                return strings.BLOCKED;
        }
    };

    lessonIsBlocked = (courseEnrollment: CourseEnrollment, lesson: Lesson): boolean => {
        if (!courseEnrollment && lesson.prerequisites) {
            return true;
        }

        if (!courseEnrollment || !courseEnrollment.lessonEnrollments || courseEnrollment.lessonEnrollments.length === 0) {
            return false;
        }
        const lessonEnrollment: LessonEnrollment = courseEnrollment.lessonEnrollments.find(
            (lEnrollment: LessonEnrollment) => lEnrollment.lesson.id === lesson.id,
        ) as LessonEnrollment;
        return !lessonEnrollment.prerequisitesMet;
    };

    canItShowCertificate = (hasCertification: boolean, lessons: LessonEnrollment[], courseStatus: string): boolean => {
        if (courseStatus === Constants.courseEnrollmentStatus.PASSED || courseStatus === Constants.courseEnrollmentStatus.COMPLETED) {
            const mandatoryLessons = lessons.filter((item) => item.lesson.mandatory);
            const concludedLessons = mandatoryLessons.filter(
            (item) =>
                item.status === Constants.lessonEnrollmentStatus.PASSED ||
                (item.status === Constants.lessonEnrollmentStatus.COMPLETED && item.lesson.type !== 'EvaluationLesson'),
            );
            return hasCertification && mandatoryLessons.length === concludedLessons.length;
        }
        return false;
    };

    attemptsLeftInEvaluation = (lesson: EvaluationLesson, lastAttemptNumber: number, score: number): number => {
        const passed = score >= lesson.masteryScore;
        const approvedButBlocked = lesson.lockAfterApproval && passed;
        const maxScore = score === 100;
        const attemptsLeft = !approvedButBlocked && !maxScore ? lesson.maxTries - lastAttemptNumber : 0;
        return attemptsLeft;
    };

    certificateUrl = (person: Person, courseEnrollment: CourseEnrollment, token: string | null): string => {
        if (!token) {
            throw new Error('Token not specified');
        }

        return Constants.FLOWLMS_COURSE_CERTIFICATE_URI.replace('%personId', (person.id || 0).toString())
            .replace('%courseEnrollmentId', courseEnrollment.id.toString())
            .replace('%token', token);
    };

    isCourseCompletedPassedOrFailed = (status: string | undefined): boolean => {
        if (status) {
            return (
                ([
                    Constants.courseEnrollmentStatus.COMPLETED,
                    Constants.courseEnrollmentStatus.PASSED,
                    Constants.courseEnrollmentStatus.FAILED,
                ] as string[]).indexOf(status) > -1
            );
        }
        return false;
    };
}

export const lmsCourseService = new LmsCourseService();
